import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  const points = [
    { k: 'hero.point1', fb: '100% Accurate Filing' },
    { k: 'hero.point2', fb: 'Refund Maximisation' },
    { k: 'hero.point3', fb: '24x7 Expert Support' },
  ];
  const stats = [
    { v: '10+', lk: 'hero.stat_years', fb: 'Years' },
    { v: '5000+', lk: 'hero.stat_clients', fb: 'Clients' },
    { v: '100%', lk: 'hero.stat_success', fb: 'Success' },
  ];

  return (
    <section className="relative pt-28 pb-20 md:pt-32 md:pb-28 hero-gradient overflow-hidden">
      <div className="absolute top-20 right-10 w-72 h-72 bg-[#14A098]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#5eead4]/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#14A098]/20 shadow-sm mb-6">
            <Sparkles className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('hero.badge', 'Trusted by 5000+ Indian taxpayers')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] text-[#0f2540] mb-6">
            {t('hero.t1', 'Professional')}{' '}
            <span className="text-[#14A098]">{t('hero.t2', 'Tax Solutions')}</span>{' '}
            {t('hero.t3', 'for Your Peace of Mind')}
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed">
            {t('hero.subtitle', 'SolveTax delivers comprehensive tax and financial services to help businesses and individuals navigate the complex world of taxation with confidence and ease.')}
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#14A098] text-[#14A098] hover:bg-[#14A098]/5 rounded-full px-8 h-12">
                {t('hero.cta_talk', 'Talk to an Expert')}
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-gray-700">
            {points.map((p) => (
              <div key={p.k} className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#14A098]" />
                <span className="font-medium">{t(p.k, p.fb)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative animate-fade-in">
          <div className="absolute -inset-4 bg-gradient-to-tr from-[#14A098]/20 to-[#5eead4]/20 rounded-3xl blur-2xl" />
          <div className="relative bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100">
            <HeroIllustration />
            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-gray-100">
              {stats.map((s) => (
                <div key={s.lk} className="text-center">
                  <div className="text-2xl md:text-3xl font-extrabold text-[#0f2540]">{s.v}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t(s.lk, s.fb)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroIllustration = () => (
  <svg viewBox="0 0 400 320" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#14A098"/><stop offset="100%" stopColor="#5eead4"/>
      </linearGradient>
      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e6fffa"/><stop offset="100%" stopColor="#ccfbf1"/>
      </linearGradient>
    </defs>
    <ellipse cx="200" cy="180" rx="180" ry="110" fill="url(#g2)"/>
    <rect x="110" y="130" width="180" height="120" rx="8" fill="#0f2540"/>
    <rect x="118" y="138" width="164" height="100" rx="4" fill="#ffffff"/>
    <rect x="100" y="248" width="200" height="10" rx="3" fill="#1a3a52"/>
    <rect x="130" y="150" width="80" height="8" rx="2" fill="#14A098"/>
    <rect x="130" y="166" width="140" height="5" rx="2" fill="#e2e8f0"/>
    <rect x="130" y="178" width="120" height="5" rx="2" fill="#e2e8f0"/>
    <rect x="130" y="190" width="90" height="5" rx="2" fill="#e2e8f0"/>
    <rect x="130" y="210" width="50" height="18" rx="4" fill="url(#g1)"/>
    <g transform="translate(290 90)">
      <circle r="34" fill="#ffffff" stroke="#14A098" strokeWidth="2"/>
      <path d="M-10 0 l7 7 l14 -14" stroke="#14A098" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <g transform="translate(60 80)">
      <rect width="70" height="50" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="10" y="28" width="8" height="14" fill="#14A098"/>
      <rect x="24" y="20" width="8" height="22" fill="#14A098"/>
      <rect x="38" y="14" width="8" height="28" fill="#14A098"/>
      <rect x="52" y="8" width="8" height="34" fill="#14A098"/>
    </g>
    <circle cx="340" cy="230" r="18" fill="#fbbf24"/>
    <text x="340" y="236" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#92400e">₹</text>
    <circle cx="55" cy="220" r="14" fill="#34d399"/>
    <text x="55" y="225" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#064e3b">%</text>
  </svg>
);

export default Hero;
